import { useState } from 'react';
import { Check, Loader2, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { TIER_PRICING, calculateTierCost, formatMoney, ANNUAL_FREE_MONTHS } from '@/lib/pricing-utils';
import { startCheckout, type TierKey, type IntervalKey } from '@/lib/billing';

interface PlanPickerProps {
  /** number of locations to bill for; user can adjust */
  defaultUnits?: number;
  compact?: boolean;
}

const PLANS: { key: TierKey; name: 'Starter' | 'Growth' | 'Pro'; blurb: string; popular?: boolean; perks: string[] }[] = [
  {
    key: 'starter',
    name: 'Starter',
    blurb: 'One kitchen, everything you need for an inspection.',
    perks: ['Digital HACCP logs', 'Inspector-ready PDF reports', 'Automatic law updates'],
  },
  {
    key: 'growth',
    name: 'Growth',
    blurb: 'Several sites in one view, with staff roles.',
    popular: true,
    perks: ['Everything in Starter', 'All locations in one dashboard', 'Staff roles and permissions'],
  },
  {
    key: 'pro',
    name: 'Pro',
    blurb: 'Custom routines and a full audit history.',
    perks: ['Everything in Growth', 'Custom checklists', 'Full audit history and exports'],
  },
];

export const PlanPicker = ({ defaultUnits = 1, compact = false }: PlanPickerProps) => {
  const [units, setUnits] = useState(Math.max(1, defaultUnits));
  const [interval, setInterval] = useState<IntervalKey>('month');
  const [busy, setBusy] = useState<TierKey | null>(null);

  const go = async (tier: TierKey) => {
    setBusy(tier);
    const res = await startCheckout(tier, units, interval);
    if (!res.ok && res.error) toast.error(res.error);
    setBusy(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Locations</span>
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setUnits(u => Math.max(1, u - 1))}>
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <span className="w-8 text-center font-semibold">{units}</span>
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setUnits(u => Math.min(200, u + 1))}>
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-1 rounded-lg border p-1">
          <button
            type="button"
            onClick={() => setInterval('month')}
            className={`px-3 py-1.5 text-sm rounded-md transition ${interval === 'month' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setInterval('year')}
            className={`px-3 py-1.5 text-sm rounded-md transition ${interval === 'year' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
          >
            Yearly · {ANNUAL_FREE_MONTHS} months free
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {PLANS.map(plan => {
          const cost = calculateTierCost(plan.name, units, interval === 'year');
          return (
            <Card
              key={plan.key}
              className={`p-5 flex flex-col relative ${plan.popular ? 'border-primary shadow-lg' : ''}`}
            >
              {plan.popular && (
                <Badge className="absolute -top-2.5 left-5">Most popular</Badge>
              )}
              <h3 className="font-semibold text-lg">{plan.name}</h3>
              <p className="text-sm text-muted-foreground mt-1 mb-4">{plan.blurb}</p>

              <div className="mb-4">
                <div className="text-2xl font-bold">
                  {formatMoney(cost.finalTotal, 'USD', 2)}
                  <span className="text-sm font-normal text-muted-foreground">
                    {' '}/ {interval === 'year' ? 'year' : 'month'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatMoney(TIER_PRICING[plan.name].unitPrice, 'USD', 2)} per location / month
                  {interval === 'year' && cost.annualSavings > 0 && ` · save ${formatMoney(cost.annualSavings, 'USD', 2)}`}
                </p>
              </div>

              {!compact && (
                <ul className="space-y-2 mb-5 flex-1">
                  {plan.perks.map(p => (
                    <li key={p} className="flex gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}

              <Button
                className="w-full mt-auto"
                variant={plan.popular ? 'default' : 'outline'}
                disabled={busy !== null}
                onClick={() => go(plan.key)}
              >
                {busy === plan.key ? <Loader2 className="h-4 w-4 animate-spin" /> : `Choose ${plan.name}`}
              </Button>
            </Card>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Secure payment through Stripe. Cancel any time.
      </p>
    </div>
  );
};

export default PlanPicker;
